import { cloneDeep, sortBy, uniqBy } from 'lodash'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { postPriceService, postService } from '../../core/services'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import { Button, Select, Spinner, Toggle } from '../atoms'
import Typography from 'components/atoms/_layouts/Typography'

declare var window: interfaces.IWindow

interface IPostPrice {
  id?: number
  age_min?: number
  age_max?: number
  price: string
  pricing_type: string
  participant_type: string
  participant_type_i18n: string
}

interface IProps {
  post: interfaces.ICustomPost
}

const participantTypes = ['adult', 'child', 'infant']

const basePostPrices = [
  {
    price: '',
    pricing_type: 'group',
    participant_type: 'adult',
    participant_type_i18n: I18n.t('generic.adult'),
  },
  {
    price: '',
    pricing_type: 'group',
    participant_type: 'child',
    participant_type_i18n: I18n.t('generic.child'),
  },
  {
    price: '',
    pricing_type: 'group',
    participant_type: 'infant',
    participant_type_i18n: I18n.t('generic.infant'),
  },
]

const isNotEmptyPrice = ({ price, age_min, age_max }) => {
  const isNotEmpty = value => value !== '' && value !== null && value !== undefined
  return isNotEmpty(price) || isNotEmpty(age_min) || isNotEmpty(age_max)
}

const PostPrice: React.FC<IProps> = props => {
  const initialPostPrice = React.useMemo(() => {
    const filtered = props.post.post_prices.filter(({ pricing_type }) => pricing_type === 'group')
    const merged = uniqBy(filtered.concat(basePostPrices), 'participant_type')
    const sorted = sortBy(merged, ({ participant_type }) =>
      participantTypes.findIndex(type => type === participant_type)
    )

    return sorted
  }, [])

  const [loading, setLoading] = React.useState(false)
  const [errors, setErrors] = React.useState({})
  const [postPrices, setPostPrices] = React.useState<IPostPrice[]>(initialPostPrice)
  const [limitParticipantType, setLimitParticipantType] = React.useState<'child' | 'infant' | null>(
    props.post.limit_participant_type
  )
  const [enableLimitAge, setEnableLimitAge] = React.useState<boolean>(
    !!props.post.limit_participant_type
  )
  const onInputChange = (type, name, value) => {
    const nextPostPrices = cloneDeep(postPrices).map(postPrice => {
      if (postPrice.participant_type === type) {
        postPrice[name] = value
      }

      return postPrice
    })

    setPostPrices(nextPostPrices)
  }

  const submit = async () => {
    if (loading) {
      return
    }

    let adultPrice = null
    const params = cloneDeep(postPrices)
      .filter(price => !isNaN(price.id) || isNotEmptyPrice(price))
      .map(({ id, pricing_type, price, age_min, age_max, participant_type }) => {
        if (participant_type === 'adult') {
          adultPrice = price
        }
        let _price = price
        const partial: any = { pricing_type, price, participant_type }
        if (id) {
          partial.id = id
        }
        if (participant_type !== 'adult' && price === '') {
          _price = adultPrice
        }
        if (age_min !== '') {
          partial.age_min = age_min
        }

        if (age_max !== '') {
          partial.age_max = age_max
        }

        if (id && price === '' && age_min === '' && age_max === '') {
          return { id, _destroy: true }
        } else {
          return { ...partial, price: _price }
        }
      })

    setLoading(true)

    try {
      const { postPrices: updatedPostPrices, flush } = await postPriceService.update(
        props.post.id,
        params
      )
      const { post } = await postService.update({
        id: props.post.id,
        limit_participant_type: limitParticipantType,
      })

      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      const updated = updatedPostPrices
        .filter(({ pricing_type }) => pricing_type === 'group')
        .map(({ id, pricing_type, price, participant_type, participant_type_i18n }) => ({
          id,
          pricing_type,
          price,
          participant_type,
          participant_type_i18n,
        }))
      const merged = uniqBy(updated.concat(basePostPrices), 'participant_type')
      const sorted = sortBy(merged, ({ participant_type }) =>
        participantTypes.findIndex(type => type === participant_type)
      )

      setPostPrices(sorted)
    } catch (e) {
      const message = e.response?.data?.errors || I18n.t('flash.unexpected_error')
      window.flashMessages.addMessage({ text: message, type: 'error' })
    } finally {
      setLoading(false)
    }
  }
  const handleSubmit = async event => {
    event.preventDefault()
    await submit()
  }

  const onChangeEnabledLimitAge = e => {
    setEnableLimitAge(e.target.checked)
    if (!e.target.checked) {
      setLimitParticipantType(null)
    }
  }

  const onUpdateLimitParticipantType = ({ target: { value } }) => {
    setLimitParticipantType(value === 'none' ? null : value)
  }
  return (
    <Wrapper>
      <h2 className="Editor_Title">{I18n.t('post.set_price')}</h2>
      <div className="main">
        <form onSubmit={handleSubmit}>
          <div className="Labels">
            <div className="type" />
            <div className="price">{I18n.t('generic.price')}</div>
            <div className="age">{I18n.t('generic.age_min')}</div>
            <div className="age">{I18n.t('generic.age_max')}</div>
          </div>
          <ul className="Prices">
            {postPrices.map(
              ({ price, age_min, age_max, participant_type, participant_type_i18n }) => (
                <li key={participant_type}>
                  <div className="type">{participant_type_i18n}</div>
                  <div className="price" key="price">
                    <input
                      type="text"
                      name="price"
                      value={price}
                      onChange={({ target: { name, value } }) =>
                        onInputChange(participant_type, name, value)
                      }
                      placeholder={
                        participant_type !== 'adult' ? I18n.t('post.same_as_adult_price') : ''
                      }
                      required={participant_type === 'adult'}
                    />
                  </div>
                  {participant_type !== 'adult' && (
                    <>
                      <div className="age" key="age-min">
                        <input
                          type="text"
                          name="age_min"
                          value={age_min}
                          onChange={({ target: { name, value } }) =>
                            onInputChange(participant_type, name, value)
                          }
                        />
                      </div>
                      <div className="age" key="age-max">
                        <input
                          type="text"
                          name="age_max"
                          value={age_max}
                          onChange={({ target: { name, value } }) =>
                            onInputChange(participant_type, name, value)
                          }
                        />
                      </div>
                    </>
                  )}
                </li>
              )
            )}
          </ul>
          <h2 className="Section_Title">Limit Age</h2>
          <Typography type="body">価格設定に年齢の入力が必須</Typography>
          <div className="LimitAge">
            <Toggle
              name={''}
              checked={enableLimitAge}
              label="Enable Limit Age"
              onChangeHandler={onChangeEnabledLimitAge}
            />
            <Select
              name=""
              label="Limit Age"
              disabled={!enableLimitAge}
              options={[
                { value: 'none', label: 'None' },
                { value: 'child', label: I18n.t('generic.child') },
                { value: 'infant', label: I18n.t('generic.infant') },
              ]}
              defaultValue={limitParticipantType}
              onChangeHandler={onUpdateLimitParticipantType}
              onBlurHandler={onUpdateLimitParticipantType}
            />
            <div className="description">
              <Typography type="body" color="subtext">
                この年齢以下のゲストが含まれていると予約ができなくなります
              </Typography>
            </div>
          </div>
          <div className="Footer">
            {loading ? <Spinner /> : <Button primary={true}>Update</Button>}
          </div>
        </form>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  ul {
    /* display: flex; */
  }

  li {
    display: flex;
    width: 80%;

    &.buttons {
      width: 240px;
    }

    .age {
      width: 40px;
    }
  }

  li + li {
    margin-top: 16px;
  }

  .Labels,
  .Prices {
    .type {
      width: 120px;
    }

    .price {
      width: 300px;
      margin-left: 12px;
    }

    .age {
      width: 100px;
      margin-left: 12px;
    }
  }
  .Section_Title {
    font-size: 16px;
    border-bottom: solid 1px ${constants.COLORS.divider};
    padding-bottom: 8px;
  }
  .Labels {
    display: flex;
  }

  .Prices {
    margin: 12px 0 24px;
  }

  .Footer {
    border-top: solid 1px ${constants.COLORS.divider};
    padding-top: 12px;
    display: flex;
    justify-content: center;
  }

  .People {
    display: flex;
    align-items: center;

    .min,
    .separator {
      width: 32px;
    }
  }

  .error {
    color: ${constants.COLORS.cautionText};
  }

  .main {
    padding: 24px;
  }
  .LimitAge {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 0;

    .description {
      text-align: right;
    }
  }
  .disabled_ages {
    padding: 24px;
  }

  input {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: solid 1px ${constants.COLORS.divider};
    border-radius: 4px;
    font-size: 15px;
    transition: border 0.2s ease;
    outline: none;
    background: none;

    &:not([readonly]):focus {
      border: solid 1px ${constants.COLORS.primary};
    }
  }
`

export default PostPrice
